// import external libraries

import { createApp } from 'vue';
import Buefy from 'buefy';

import '@/assets/images/fontawesome.js';

// noinspection ES6UnusedImports
import _ from 'lodash-es';

import * as Sentry from '@sentry/vue';
import { Vue3Mq } from 'vue3-mq';
import AutoRotate from 'vue-jpeg-auto-rotation';
import { definePreset } from '@primevue/themes';

// import PrimeVue components
import PrimeVue from 'primevue/config';
import Lara from '@primevue/themes/lara';
import Dropdown from 'primevue/dropdown';
import Chart from 'primevue/chart';
import ColorPicker from 'primevue/colorpicker';
import Column from 'primevue/column';
import Button from 'primevue/button';
import Popover from 'primevue/popover';
import Menu from 'primevue/menu';
import AvatarGroup from 'primevue/avatargroup';
import Galleria from 'primevue/galleria'; // Marketplace Modal
import DataTable from 'primevue/datatable'; // Supervisor Dashboard
import Dialog from 'primevue/dialog';
import Toast from 'primevue/toast';
import SelectButton from 'primevue/selectbutton';
import Select from 'primevue/select';
import ToastService from 'primevue/toastservice';
import TieredMenu from 'primevue/tieredmenu';
import Card from 'primevue/card';
import TreeSelect from 'primevue/treeselect';
import Message from 'primevue/message';
import Badge from 'primevue/badge';
import OverlayBadge from 'primevue/overlaybadge';
import ToggleButton from 'primevue/togglebutton';
import ProgressSpinner from 'primevue/progressspinner';
import ProgressBar from 'primevue/progressbar';
import Tag from 'primevue/tag';
import Ripple from 'primevue/ripple';
import PAvatar from 'primevue/avatar';
import IconField from 'primevue/iconfield';
import InputIcon from 'primevue/inputicon';
import InputNumber from 'primevue/inputnumber';
import InputText from 'primevue/inputtext';
import SplitButton from 'primevue/splitbutton';
import PickList from 'primevue/picklist';
import Drawer from 'primevue/drawer';
import ConfirmDialog from 'primevue/confirmdialog';
import Skeleton from 'primevue/skeleton';
import InputGroup from 'primevue/inputgroup';
import InputGroupAddon from 'primevue/inputgroupaddon';
import RadioButton from 'primevue/radiobutton';
import CascadeSelect from 'primevue/cascadeselect';
import MultiSelect from 'primevue/multiselect';
import Divider from 'primevue/divider';
import Tooltip from 'primevue/tooltip';
import ConfirmationService from 'primevue/confirmationservice';
import Accordion from 'primevue/accordion';
import AccordionPanel from 'primevue/accordionpanel';
import AccordionHeader from 'primevue/accordionheader';
import AccordionContent from 'primevue/accordioncontent';
import Paginator from 'primevue/paginator';
import 'primeicons/primeicons.css';

import VueConfetti from 'vue-confetti';
import { FontAwesomeIcon, FontAwesomeLayers } from '@fortawesome/vue-fontawesome';

// import internal Libraries
// noinspection ES6UnusedImports
import App from './App.vue';
import eventBus from '@/plugins/event_bus';
import breadcrumbs from '@/plugins/breadcrumbs/breadcrumbs';
import Avatar from '@/plugins/avatar/Avatar.vue';
import FormatCurrency from '@/app/shared_components/currency/FormatCurrency.vue';
import GlobalConfirmModal from '@/app/shared_components/modals/GlobalConfirmModal.vue';
import buttons from '@/app/shared_components/buttons/buttons.js';
import i18n from '@/i18n/index';
import router from '@/router/index';
import { AuthPlugin } from '@/auth';
import store from '@/store/index';
import toasts from '@/app/util/toasts';
import sequentialPromise from '@/app/util/synchronous_promise';
import VPopper from '@/app/shared_components/tooltips/VPopper.vue';
import TitleSmall from '@/app/shared_components/titles/TitleSmall.vue';
import TitleMedium from '@/app/shared_components/titles/TitleMedium.vue';
import TitleForm from '@/app/shared_components/titles/TitleForm.vue';
import Modal from '@/app/shared_components/modals/TheModal.vue';
import truncate from '@/plugins/truncate';

// import constants
import { ZIGGU_API_URL } from '@/app/util/urls';
import flipper from '@/plugins/flipper';
import { FT } from '@/app/data/flipper_constants';

// import firebase
import { initializeApp } from 'firebase/app';

import { getAnalytics } from 'firebase/analytics';

// import css stylesheets
import '@/assets/css/index.css';
import '@/assets/css/style.css';
import '@/assets/css/vue-popper.css';
import '@/assets/css/ziggu.css';
import AppLink from '@/app/shared_components/AppLink.vue';
import { ToggleSwitch } from 'primevue';

const app = createApp(App);

app.use({
  install() {
    app.config.globalProperties.emitter = eventBus;
    app.provide('emitter', eventBus);
  },
});

app.use(store);
app.use(router);
app.use(i18n);

app.use(Buefy, { defaultIconPack: 'fas' });

// Initialize Sentry
if (!import.meta.env.IS_LOCAL) {
  Sentry.init({
    app,
    dsn: 'https://be16e9a319304d88b103e2300a4f669f@sentry.io/1190623',
    release: `${import.meta.env.RELEASE_VERSION}`,
    environment: `${import.meta.env.VITE_ENV}`,
    ignoreErrors: [
      'TypeError: NetworkError when attempting to fetch resource.',
      'Non-Error exception captured',
      'Non-Error promise rejection captured',
      'ResizeObserver loop completed with undelivered notifications.',
      // Ignore MutationObserver error from vue3-popper
      "Failed to execute 'observe' on 'MutationObserver': parameter 1 is not of type 'Node'.",
      // https://github.com/nico3333fr/CSP-useful/blob/master/csp-wtf/explained.md#apple-quicklook
      "Refused to evaluate a string as JavaScript because 'unsafe-eval' is not " +
        'an allowed source of script in the following Content Security Policy directive: ' +
        '"default-src x-apple-ql-id: \'unsafe-inline\'"',
      // expired Google Cloud Storage URLs for PDFs
      'Unexpected server response (400) while retrieving PDF',
      // PDF-related errors
      'InvalidPDFException',
      /Invalid PDF structure/i,
      /UnknownErrorException: Load failed/i,
      // Safari/iOS IndexedDB instability during Firebase Auth init
      /Database deleted by request of the user/i,
      /UnknownError:.*Database deleted by request of the user/i,
      /UnknownError: Connection to Indexed Database server lost/i,
      // Network-related errors
      /^Network Error$/i,
      /Failed to fetch/i,
      /NetworkError when attempting to fetch resource/i,
    ],
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    integrations: [
      Sentry.browserTracingIntegration({
        router,
        tracePropagationTargets: [`${ZIGGU_API_URL.replace('https://', '')}`],
      }),
      Sentry.replayIntegration(),
    ],
    tracesSampleRate: 0.25, // percentage
    normalizeDepth: 8,
    // prevent 401 error logs (expired sessions, see api/index.js) being sent to Sentry
    beforeSend(event, hint) {
      const isUnauthorizedError =
        hint &&
        hint.originalException &&
        hint.originalException.response &&
        hint.originalException.response.status &&
        // 401: user unauthorized (legitimate in general)
        // 403: resource unauthorized (legitimate in general, e.g. stale data due to cache)
        _.includes([401, 403], hint.originalException.response.status);
      const isSentryDiscard =
        hint &&
        hint.originalException &&
        hint.originalException.sentry_discard &&
        hint.originalException.sentry_discard === true;
      const isAPIError =
        hint &&
        hint.originalException &&
        hint.originalException.response &&
        hint.originalException.response.status &&
        // 500: API raised an unexpected error, Sentry will log this on API-side, logging of APP side error is redundant
        hint.originalException.response.status === 500;
      const isChunkError =
        (event.message && event.message.startsWith('Loading chunk ')) ||
        (event.name && event.name === 'ChunkLoadError');
      // Filter out errors only when www appears before a subdomain (e.g., www.subdomain.domain.com)
      const hostnameParts = window.location.hostname.split('.');
      const isWWWSubdomain = hostnameParts.length > 2 && hostnameParts[0] === 'www';
      // ignore network errors in general, typically noise, mostly due to Safari https://github.com/axios/axios/issues/4420
      const isNetworkError =
        (event.name && event.name === 'AxiosError' && event.message && event.message === 'Network Error') ||
        (hint &&
          hint.originalException &&
          hint.originalException.message &&
          (hint.originalException.message === 'Network Error' ||
            hint.originalException.message.includes('Failed to fetch') ||
            hint.originalException.message.includes('NetworkError')));

      if (isUnauthorizedError || isSentryDiscard || isChunkError || isNetworkError || isAPIError || isWWWSubdomain) {
        return null;
      }

      return event;
    },
  });
} else {
  app.config.performance = true;
}

// Initialize authentication
app.use(AuthPlugin);

// Initialize Firebase
const app_firebase = initializeApp({
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
});
getAnalytics(app_firebase);

app.use({
  install() {
    window._ = _;
    app.config.globalProperties.window = window;
    app.config.globalProperties.$_ = _;
    app.config.globalProperties.$FT = FT;
  },
});
app.config.globalProperties.sequentialPromise = sequentialPromise;

app.use(VueConfetti);
app.mixin(toasts);

app.component('Avatar', Avatar);
app.component('FormatCurrency', FormatCurrency);
app.component('FontAwesomeIcon', FontAwesomeIcon);
app.component('GlobalConfirmModal', GlobalConfirmModal);
app.component('ProgressSpinner', ProgressSpinner);
app.component('ProgressBar', ProgressBar);
app.component('FontAwesomeLayers', FontAwesomeLayers);
app.component('Chart', Chart);
app.component('Column', Column);
app.component('Modal', Modal);
app.component('Popover', Popover);
app.component('VPopper', VPopper);
app.component('TitleSmall', TitleSmall);
app.component('TitleMedium', TitleMedium);
app.component('TitleForm', TitleForm);
app.component('Dropdown', Dropdown);
app.component('Message', Message);
app.component('InputText', InputText);
app.component('TieredMenu', TieredMenu);
app.component('Tag', Tag);
app.component('InputIcon', InputIcon);
app.component('IconField', IconField);
app.component('AvatarGroup', AvatarGroup);
app.component('Skeleton', Skeleton);
app.component('Galleria', Galleria);
app.component('Card', Card);
app.component('DataTable', DataTable);
app.component('ColorPicker', ColorPicker);
app.component('PrimeDialog', Dialog);
app.component('PickList', PickList);
app.component('SelectButton', SelectButton);
app.component('ToggleButton', ToggleButton);
app.component('ToggleSwitch', ToggleSwitch);
app.component('PSelect', Select);
app.component('Badge', Badge);
app.component('OverlayBadge', OverlayBadge);
app.component('TreeSelect', TreeSelect);
app.component('ConfirmDialog', ConfirmDialog);
app.component('Toast', Toast);
app.component('InputNumber', InputNumber);
app.component('InputGroup', InputGroup);
app.component('InputGroupAddon', InputGroupAddon);
app.component('RadioButton', RadioButton);
app.component('Drawer', Drawer);
app.component('PButton', Button);
app.component('PMenu', Menu);
app.component('PAvatar', PAvatar);
app.component('CascadeSelect', CascadeSelect);
app.component('MultiSelect', MultiSelect);
app.component('Divider', Divider);
app.component('SplitButton', SplitButton);
app.component('Accordion', Accordion);
app.component('AccordionPanel', AccordionPanel);
app.component('AccordionHeader', AccordionHeader);
app.component('AccordionContent', AccordionContent);
app.component('Paginator', Paginator);
app.component('AppLink', AppLink);
app.directive('ripple', Ripple);
app.directive('tooltip', Tooltip);

const MyPreset = definePreset(Lara, {
  semantic: {
    primary: {
      50: '{blue.50}',
      100: '{blue.100}',
      200: '{blue.200}',
      300: '{blue.300}',
      400: '{blue.400}',
      500: '{blue.500}',
      600: '{blue.600}',
      700: '{blue.700}',
      800: '{blue.800}',
      900: '{blue.900}',
      950: '{blue.950}',
    },
  },
});

app.use(PrimeVue, {
  // Default theme configuration
  theme: {
    preset: MyPreset,
    options: {
      darkModeSelector: 'none',
    },
  },
});

app.use(AutoRotate);
app.use(breadcrumbs);
// router.beforeEach((to, from, next) => {
//     if (to.path !== from.path) {
//         const bc = app.config.globalProperties.$breadcrumbs;
//         if (bc) bc.reset();
//     }
//     next();
// });
app.use(flipper, store);
app.use(buttons);
app.use(Vue3Mq);
app.use(ToastService);
app.use(ConfirmationService);

const truncatePlugin = {
  install() {
    app.truncate = truncate;
    app.config.globalProperties.$truncate = truncate;
  },
};
app.use(truncatePlugin);

// load error handling for active sessions (silent reload the page)
// https://vite.dev/guide/build#load-error-handling
window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault();
  window.location.reload();
});

app.mount('#app');
